import { useEffect } from 'react';
import { Platform } from 'react-native';
import type { CalendarEvent } from '../../../types/calendar';
import { useEventDrag } from '../hooks/useEventDrag';
import type { LocalDate } from '../utils/calendarDate';
import { localMinutesSinceMidnight, type TimeWindow } from '../utils/calendarDayGeometry';
import { spanToInstants } from '../utils/calendarDragSnap';
import type { EventBlockLayout } from '../utils/calendarEventLayout';
import { CalendarDayEventBlock } from './CalendarDayEventBlock';

interface DraggableEventBlockProps {
  event: CalendarEvent;
  date: LocalDate;
  timeZone: string;
  window: TimeWindow;
  gridHeight: number;
  layout: EventBlockLayout;
  clippedStart?: boolean;
  clippedEnd?: boolean;
  onPress?: (event: CalendarEvent) => void;
  /** Receives instants, already resolved against the event's own zone. */
  onReschedule?: (event: CalendarEvent, span: { startsAt: string; endsAt: string }) => void;
}

/**
 * A positioned event block that can be dragged to a new time — on the desktop.
 *
 * The move/up listeners live on `window` rather than on the block: a drag that
 * leaves the block (which is only a few dozen pixels tall) must still track
 * and still finish, and a mouse released outside it must not strand the
 * gesture. They are attached only while a drag is in flight.
 *
 * Cancelled and all-day events are not draggable: an all-day block is not
 * positioned by time at all, and rescheduling something already cancelled is
 * not a gesture anyone means to make.
 */
export function DraggableEventBlock({
  event,
  date,
  timeZone,
  window: dayWindow,
  gridHeight,
  layout,
  clippedStart,
  clippedEnd,
  onPress,
  onReschedule,
}: Readonly<DraggableEventBlockProps>) {
  const span: TimeWindow = {
    startMinutes: localMinutesSinceMidnight(event.startsAt, date, timeZone),
    endMinutes: localMinutesSinceMidnight(event.endsAt, date, timeZone),
  };

  const drag = useEventDrag({
    span,
    window: dayWindow,
    gridHeight,
    enabled: Boolean(onReschedule) && !event.allDay && event.status !== 'cancelled',
    // Converted here, where the day and the zone are both known. Doing it in
    // the route would have to guess at a day and would reach for the DEVICE
    // offset, shifting every dragged meeting for a viewer in another zone.
    onCommit: (next) => onReschedule?.(event, spanToInstants(date, next, event.timeZone)),
  });

  const { isDragging, onDragMove, onDragEnd, onDragCancel } = drag;

  useEffect(() => {
    if (Platform.OS !== 'web' || !isDragging) return;
    // `globalThis` rather than the RN `window` import name, which this file
    // shadows with the day window prop.
    const view = globalThis as unknown as {
      addEventListener?: (t: string, h: (e: { pageY: number }) => void) => void;
      removeEventListener?: (t: string, h: (e: { pageY: number }) => void) => void;
    };
    if (!view.addEventListener) return;

    const handleMove = (nativeEvent: { pageY: number }) => onDragMove(nativeEvent.pageY);
    const handleUp = () => onDragEnd();

    view.addEventListener('mousemove', handleMove);
    view.addEventListener('mouseup', handleUp);

    return () => {
      view.removeEventListener?.('mousemove', handleMove);
      view.removeEventListener?.('mouseup', handleUp);
    };
  }, [isDragging, onDragMove, onDragEnd]);

  // Unmounting mid-drag (a refetch replacing the list) must not leave the
  // gesture running against an event that is gone.
  useEffect(() => onDragCancel, [onDragCancel]);

  const preview = drag.previewSpan;
  const windowSpan = dayWindow.endMinutes - dayWindow.startMinutes;
  const previewStyle =
    preview && windowSpan > 0
      ? {
          top: Math.round(
            ((preview.startMinutes - dayWindow.startMinutes) / windowSpan) * gridHeight,
          ),
          height: Math.max(
            Math.round(((preview.endMinutes - preview.startMinutes) / windowSpan) * gridHeight) - 2,
            1,
          ),
        }
      : null;

  return (
    <CalendarDayEventBlock
      event={event}
      onPress={onPress}
      drag={drag}
      clippedStart={clippedStart}
      clippedEnd={clippedEnd}
      style={{
        position: 'absolute',
        ...layout,
        // Mid-drag the block follows the cursor; the committed position comes
        // back through the optimistic cache write, not from here.
        ...(previewStyle ?? {}),
      }}
    />
  );
}
